/**
 * Le crochet de la porte du quiz de prérequis : il branche la machine (`prerequisiteGate`) sur l'API
 * du quiz, la progression gardée et la télémétrie, et rend ce que la conversation du quiz demande
 * (`conversation`, `api`) à l'écran, qui la monte par `useCheckpointConversation`.
 *
 * Ce qui part d'ici :
 *  - « Commencer le quiz » : `GET activities/{id}/quiz` ; une tentative déjà faite ouvre l'activité, un
 *    quiz introuvable (404) ou sans question aussi (son incident part dans Bugsnag, `unavailable`) ;
 *  - chaque réponse, gardée dans `liveMode.quizProgress` (persisté, partagé avec l'ancien écran : un
 *    quiz interrompu reprend où il en était, d'un écran à l'autre) ;
 *  - la dernière : `POST …/quiz/attempt` (avec la séance, en live, pour que le tuteur reçoive le
 *    bilan), la progression effacée, `quiz.completed` vers Amplitude comme avant, l'élève relu (la
 *    todo dit la tentative), l'activité courante mise à jour si c'est la nôtre ;
 *  - un fil d'Ariane à chaque changement d'état, une erreur typée à chaque échec, aucun `catch` vide.
 *
 * La relecture du quiz (`useQuizReview`) vit avec la porte : la tentative faite, la feuille s'ouvre sur
 * la correction que la réponse porte, et l'activité attend qu'elle se ferme, comme sous l'ancien voile ;
 * plus tard, « Revoir le quiz » la rouvre (`review.open`), par-dessus l'activité qui continue.
 *
 * L'état rendu suit l'activité passée au même rendu : une autre activité ne montre jamais, le temps
 * d'un rendu, la porte (ouverte ou fermée) de la précédente.
 */
import * as amplitude from "@amplitude/analytics-react-native"
import { useCallback, useEffect, useMemo, useRef } from "react"
import { useDispatch, useStore } from "react-redux"

import { setCurrentActivity } from "../../../data/redux/actions/activity"
import { clearQuizProgress, setQuizProgress } from "../../../data/redux/actions/liveMode"
import { activitiesService } from "../../../data/services/activitiesService"
import { authService } from "../../../data/services/authService"
import { defaultCheckpointApi, type CheckpointApi } from "../../precall/checkpoint/checkpointApi"
import { type ApiCheckpointConversation } from "../../precall/checkpoint/checkpointConversationMachine"
import { type PhaseTransition, useSignalReducer, withPhaseTransitions } from "../../shared/useSignalReducer"
import { apiCall } from "../../shared/apiCall"
import { createTelemetry, httpStatusOf, type EngineTelemetry } from "../../shared/telemetry"
import {
    gateBlocks,
    initialGateState,
    isRemovedQuizResponse,
    prerequisiteGateReducer,
    type ApiQuiz,
    type ApiQuizAttemptResponse,
    type PrerequisiteActivity,
    type PrerequisiteGateState,
    type QuizAnswer,
    type QuizProgress,
} from "./prerequisiteGate"
import { createQuizCheckpointApi } from "./quizCheckpointApi"
import { canReviewQuiz, type ApiQuizForReview } from "./quizReview"
import { useQuizReview, type QuizReviewHandle } from "./useQuizReview"

/** Les appels du quiz (les tests passent les leurs). */
export type PrerequisiteQuizServices = {
    /** Le quiz, et, une fois la tentative faite, sa correction (`QuizResource`). */
    getQuiz: (activityId: number) => Promise<(ApiQuiz & ApiQuizForReview) | null>
    submitAttempt: (
        activityId: number,
        body: { answers: QuizAnswer[]; live_session_id: number | null }
    ) => Promise<ApiQuizAttemptResponse>
    /** L'élève relu : la todo porte la tentative. */
    refreshUser: () => Promise<unknown>
}

/** La progression d'un quiz interrompu, par conversation. */
export type QuizProgressStore = {
    read: (conversationId: number) => QuizProgress | null
    save: (conversationId: number, answers: QuizAnswer[], nextIndex: number) => void
    clear: (conversationId: number) => void
}

export type UsePrerequisiteGateOptions = {
    activity: PrerequisiteActivity | null
    /** En live : jointe à la tentative, pour que le tuteur reçoive le bilan. */
    liveSessionId?: number | null
    /** La tentative est faite (le live y prévient le tuteur). */
    onQuizCompleted?: (result: ApiCheckpointConversation) => void
    services?: PrerequisiteQuizServices
    progress?: QuizProgressStore
    checkpointApi?: CheckpointApi
    telemetry?: EngineTelemetry
}

export type PrerequisiteGate = {
    state: PrerequisiteGateState
    /** L'activité est arrêtée : la porte, ou le quiz ouvert (le chrono, la page sous le voile). */
    blocking: boolean
    /** « Commencer le quiz », ou « Réessayer ». */
    start: () => void
    /** La conversation du quiz ouvert, et ses appels : `useCheckpointConversation({ conversation, api })`. */
    api: CheckpointApi | null
    /** La conversation du quiz s'est fermée. */
    closed: () => void
    /** La relecture du quiz : à la fin du quiz, puis « Revoir le quiz ». */
    review: QuizReviewHandle
    /** L'élève a fait sa tentative : « Revoir le quiz » s'offre. */
    canReview: boolean
}

type Envelope<T> = { data?: T & { data?: T } }

const defaultServices = (dispatch: (action: never) => unknown): PrerequisiteQuizServices => ({
    getQuiz: async (activityId) => {
        const response = (await activitiesService.getPrerequisiteQuiz({ activityId })) as Envelope<ApiQuiz>
        return response?.data?.data ?? response?.data ?? null
    },
    submitAttempt: async (activityId, body) => {
        const response = (await activitiesService.submitPrerequisiteQuizAttempt(
            { activityId },
            body
        )) as Envelope<ApiQuizAttemptResponse>
        return (response?.data as ApiQuizAttemptResponse) ?? {}
    },
    refreshUser: () => apiCall(dispatch, authService.getUser as never),
})

type ProgressRoot = { liveMode?: { quizProgress?: Record<string, QuizProgress> } }
type CurrentActivityRoot = {
    data?: { currentActivity?: { activity?: { activityData?: PrerequisiteActivity | null } | null } }
}

const transitionLabel = (state: PrerequisiteGateState) =>
    state.loading ? `${state.status}:loading` : state.reading ? `${state.status}:reading` : state.status

const reduceWithTransitions = withPhaseTransitions(prerequisiteGateReducer, transitionLabel)

export function usePrerequisiteGate(options: UsePrerequisiteGateOptions): PrerequisiteGate {
    const { activity } = options
    const dispatchRedux = useDispatch()
    const store = useStore()
    const optionsRef = useRef(options)
    optionsRef.current = options

    const telemetry = useMemo(
        () => options.telemetry ?? createTelemetry("activity.prerequisite", { role: "student" }),
        // La télémétrie d'une porte montée ne change pas en route.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    )
    const services = useMemo(
        () => options.services ?? defaultServices(dispatchRedux as never),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [dispatchRedux]
    )
    const progress = useMemo<QuizProgressStore>(
        () =>
            options.progress ?? {
                read: (id) => (store.getState() as ProgressRoot).liveMode?.quizProgress?.[id] ?? null,
                save: (id, answers, nextIndex) => dispatchRedux(setQuizProgress(id, answers, nextIndex)),
                clear: (id) => dispatchRedux(clearQuizProgress(id)),
            },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [store, dispatchRedux]
    )

    const [stored, dispatch, read] = useSignalReducer(
        reduceWithTransitions,
        () => initialGateState(activity),
        (transitions: PhaseTransition[]) => {
            for (const { from, to, event } of transitions) telemetry.transition(from, to, event)
        }
    )

    // L'activité passée fait foi au rendu même ; la machine la rejoint juste après.
    useEffect(() => {
        dispatch({ type: "activity", activity })
    }, [activity, dispatch])
    const state = stored.activityId === (activity?.id ?? null) ? stored : initialGateState(activity)

    const review = useQuizReview({ activityId: activity?.id ?? null, getQuiz: services.getQuiz, telemetry })
    const reviewRef = useRef(review)
    reviewRef.current = review

    /** `GET activities/{id}/quiz`, avant l'intro ou au toucher : la machine sait lequel des deux. */
    const load = useCallback(
        (id: number) => {
            void (async () => {
                try {
                    const quiz = await services.getQuiz(id)
                    if (read().activityId !== id) return
                    const saved = quiz?.conversation_id != null ? progress.read(quiz.conversation_id) : null
                    dispatch({ type: "quizLoaded", quiz, saved })
                    // Un quiz vide ouvre l'activité : l'incident se lit dans Bugsnag, pas dans l'écran.
                    if (read().bypassed) {
                        telemetry.error("unavailable", new Error("quiz sans question ni conversation"), {
                            reason: "empty",
                            questions: quiz?.questions?.length ?? 0,
                        })
                    }
                } catch (error) {
                    if (read().activityId !== id) return
                    // Introuvable (404) : l'activité s'ouvre, l'incident part dans Bugsnag — sauf un quiz que
                    // le masteur a retiré, son geste. Le reste (le réseau, le serveur) garde « Réessayer ».
                    const missing = httpStatusOf(error) === 404
                    if (!missing) telemetry.error("load_quiz", error)
                    else if (!isRemovedQuizResponse(error))
                        telemetry.error("unavailable", error, { reason: "not_found" })
                    dispatch({ type: "quizLoadFailed", missing })
                }
            })()
        },
        [dispatch, progress, read, services, telemetry]
    )

    // Le quiz se lit avant l'intro : introuvable, vide ou déjà tenté, l'activité s'ouvre sans elle.
    useEffect(() => {
        const current = read()
        const id = current.activityId
        if (id == null || current.status !== "locked" || current.read || current.reading || current.loading) return
        dispatch({ type: "readRequested" })
        load(id)
    }, [state.activityId, state.status, state.read, dispatch, load, read])

    const start = useCallback(() => {
        const current = read()
        const id = current.activityId
        if (id == null || current.loading || (current.status !== "locked" && current.status !== "error")) return
        dispatch({ type: "startRequested" })
        // La lecture d'avant l'intro est encore en vol : elle ouvrira le quiz.
        if (current.reading) return
        // Déjà lu : il s'ouvre sans être relu, à la question où l'élève en était.
        const quiz = current.status === "locked" ? current.quiz : null
        if (quiz != null) {
            const saved = quiz.conversation_id != null ? progress.read(quiz.conversation_id) : null
            dispatch({ type: "quizLoaded", quiz, saved })
            return
        }
        load(id)
    }, [dispatch, load, progress, read])

    const onAttempt = useCallback(
        (activityId: number, quiz: ApiQuiz, response: ApiQuizAttemptResponse, result: ApiCheckpointConversation) => {
            if (read().activityId !== activityId) return
            dispatch({ type: "attemptSubmitted" })
            // La correction s'ouvre dès que le quiz se ferme : la réponse la porte (sinon elle est relue).
            reviewRef.current.attempted(response.quiz ?? null)
            if (quiz.conversation_id != null) progress.clear(quiz.conversation_id)
            const attempt = response.data ?? {}
            const total = attempt.total ?? quiz.questions?.length ?? 0
            amplitude.track("quiz.completed", {
                passed: attempt.passed,
                score: attempt.score ?? 0,
                total,
                percentage: attempt.percentage ?? (total > 0 ? ((attempt.score ?? 0) / total) * 100 : 0),
                activity_id: activityId,
            })
            services.refreshUser().catch((error: unknown) => telemetry.error("refresh_user", error))
            // L'activité courante (la route `activity` la relit au retour) dit la tentative, si c'est la nôtre.
            const current = (store.getState() as CurrentActivityRoot).data?.currentActivity?.activity?.activityData
            if (current?.id === activityId) {
                dispatchRedux(
                    setCurrentActivity({
                        activityData: { ...current, quiz_status: attempt.passed ? "passed" : "failed" },
                        progressStatus: "ongoing",
                    })
                )
            }
            // Le tuteur reçoit la conversation du serveur, qui porte le score ; celle de l'élève n'a pas de résumé.
            optionsRef.current.onQuizCompleted?.(
                response.conversation
                    ? { ...result, conversation: { ...result.conversation, ...response.conversation } }
                    : result
            )
        },
        [dispatch, dispatchRedux, progress, read, services, store, telemetry]
    )

    // La conversation d'une ouverture du quiz, et ses appels : neufs à chaque ouverture.
    const baseApi = options.checkpointApi ?? defaultCheckpointApi
    const quizOpen = stored.status === "quiz"
    const api = useMemo(() => {
        const opened = read()
        if (opened.status !== "quiz" || opened.quiz == null || opened.activityId == null) return null
        const { quiz, activityId } = opened
        return createQuizCheckpointApi({
            quiz,
            startIndex: opened.startIndex,
            answers: opened.answers,
            base: baseApi,
            submit: async (answers) => {
                try {
                    return await services.submitAttempt(activityId, {
                        answers,
                        live_session_id: optionsRef.current.liveSessionId ?? null,
                    })
                } catch (error) {
                    // Le masteur a retiré ce quiz et l'app a manqué son message : l'énoncé s'ouvre,
                    // au lieu d'une tentative refusée en boucle.
                    if (isRemovedQuizResponse(error)) dispatch({ type: "quizRemoved" })
                    else telemetry.error("submit_attempt", error, { answers: answers.length })
                    throw error
                }
            },
            onProgress: (answers, nextIndex) => {
                if (quiz.conversation_id != null) progress.save(quiz.conversation_id, answers, nextIndex)
            },
            onAttempt: (response, result) => onAttempt(activityId, quiz, response, result),
        })
        // Une ouverture = une session : l'api se crée avec elle.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stored.session, quizOpen])

    const closed = useCallback(() => dispatch({ type: "quizClosed" }), [dispatch])

    return {
        state,
        blocking: gateBlocks(state) || review.blocking,
        start,
        api: state.status === "quiz" ? api : null,
        closed,
        review,
        canReview: !gateBlocks(state) && canReviewQuiz(activity, state.attempted),
    }
}
